import React, { useState, useEffect } from "react";
import Layout from "../../components/Layout/Layout";
import ConfirmModal from "../../components/Modals/ConfirmModal";
import CustomerServices from "../../services/CustomerServices";
import { HollowDotsSpinner } from "react-epic-spinners";
import { SearchIcon } from "@heroicons/react/solid";
import { ArrowCircleDownIcon, ArrowRightIcon, RefreshIcon } from "@heroicons/react/outline";
import DatePicker from "react-date-picker";
import Paginate from "../../utils/Paginate";
import { convert, formatDate } from "../../utils/convertDate";
import fire from "../../utils/Toastr";

const serviceStyles = {
  NIN: "bg-rose-100 text-rose-700",
  BVN: "bg-blue-100 text-blue-700",
  FLEXCUBE: "bg-amber-100 text-amber-700",
};

const statusStyles = {
  None: "bg-gray-100 text-gray-700",
  Pending: "bg-amber-100 text-amber-700",
  Initiated: "bg-blue-100 text-blue-700",
  Completed: "bg-green-100 text-green-700",
  Failed: "bg-red-100 text-red-700",
};

export default function OnboardingStatusHistory() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const [exportLoading, setExportLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [formValues, setFormValues] = useState({ from: null, to: null });
  const [fromm, setFromm] = useState("");
  const [too, setToo] = useState("");
  const [confirmState, setConfirmState] = useState(null);
  const [adminType, setAdminType] = useState("");
  const [pagination, setPagination] = useState({
    pageIndex: 1,
    pageSize: 10,
    totalPages: 1,
    totalCount: 0,
    hasPreviousPage: false,
    hasNextPage: false,
  });

  const pageSize = 10;

  const fetchRecords = async (page = 1, filters = {}) => {
    const searchTerm = filters.search ?? search;
    const from = filters.from ?? fromm;
    const to = filters.to ?? too;
    setLoading(true);
    try {
      const response = await CustomerServices.listOnboardingStatusHistory(
        page,
        pageSize,
        searchTerm,
        from,
        to
      );
      if (response && response.data) {
        setRecords(response.data.result || []);
        setPagination({
          pageIndex: response.data.pageIndex,
          pageSize,
          totalPages: response.data.totalPages,
          totalCount: response.data.totalCount,
          hasPreviousPage: response.data.hasPreviousPage,
          hasNextPage: response.data.hasNextPage,
        });
      }
    } catch (error) {
      console.error("Error fetching onboarding status history:", error);
      fire("error", "Failed to load onboarding status history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    var details = sessionStorage.getItem("user");
    if (details) {
      var userDetails = JSON.parse(details);
      setAdminType(userDetails?.adminType || "");
    }
    const oldFrom = new Date().setDate(new Date().getDate() - 7);
    const from = oldFrom ? convert(formatDate(new Date(oldFrom))) : "";
    const to = convert(formatDate(new Date()));
    fetchRecords(1, { from, to });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isMaker = adminType.toLowerCase() === "maker";

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, pageIndex: newPage }));
    fetchRecords(newPage);
  };

  const handleDateFilter = async (e) => {
    e.preventDefault();
    const from = formValues.from ? convert(formatDate(formValues.from)) : "";
    const to = formValues.to ? convert(formatDate(formValues.to)) : "";
    setFromm(from);
    setToo(to);
    fetchRecords(1, { from, to });
  };

  const exportCSV = async () => {
    setExportLoading(true);
    try {
      const resp = await CustomerServices.exportOnboardingStatusHistory(
        pagination.pageIndex,
        pageSize,
        search,
        fromm,
        too
      );
      if (resp && resp.data) {
        window.open(resp.data);
      } else {
        fire("error", resp?.message || "Export failed");
      }
    } catch (error) {
      console.error("Error exporting onboarding status history:", error);
      fire("error", "Failed to export onboarding status history");
    } finally {
      setExportLoading(false);
    }
  };

  const getActionDetails = (record) => {
    const status = record.adminStatus || "None";
    if (status === "Completed") return null;

    const service = String(record.serviceAffected || "").toUpperCase();
    const details = {};

    if (service === "NIN") {
      details.sync = {
        action: "NIN",
        label: "Sync & Re-initate",
        title: "Sync NIN and re-initate onboarding",
        message: `This will re-run the NIN sync for ${
          record.uniqueIdentifier || record.phoneNumber
        } and then re-initate onboarding. Continue?`,
      };
    }
    if (service === "BVN") {
      details.sync = {
        action: "BVN",
        label: "Sync & Re-initate",
        title: "Sync BVN and re-initate onboarding",
        message: `This will re-run the BVN sync for ${
          record.uniqueIdentifier || record.phoneNumber
        } and then re-initate onboarding. Continue?`,
      };
    }
    if (service === "FLEXCUBE") {
      details.sync = {
        action: "FLEXCUBE",
        label: "Re-initate",
        title: "Re-initate onboarding",
        message: `This will re-initate onboarding for ${
          record.uniqueIdentifier || record.phoneNumber
        }. Continue?`,
      };
    }

    return details;
  };

  const openConfirm = (record) => {
    const details = getActionDetails(record);
    if (!details || !details.sync) {
      fire("error", "Unsupported service affected");
      return;
    }
    setConfirmState({ record, details: details.sync });
  };

  const isFailure = (res) =>
    !res || res.status === false || res.status === "error";

  const runReInitiate = async (record) => {
    return await CustomerServices.reInitiateOnboarding(
      {
        userId: record.userId ?? 0,
        uniqueIdentifier: record.uniqueIdentifier ?? null,
        userName: record.userName ?? null,
      },
      true
    );
  };

  const confirmAction = async () => {
    if (!confirmState) return;
    const { record, details } = confirmState;
    const id = record.id;

    if (actionLoading[id]) return;

    setActionLoading((prev) => ({ ...prev, [id]: true }));
    let succeeded = false;
    try {
      let syncResult = null;
      if (details.action === "NIN") {
        syncResult = await CustomerServices.syncNinDetails(
          {
            nin: record.uniqueIdentifier ?? null,
            userName: record.userName ?? null,
          },
          true
        );
        if (isFailure(syncResult)) {
          fire("error", syncResult?.message || "NIN sync failed");
          return;
        }
        fire("success", "NIN sync completed successfully");
      } else if (details.action === "BVN") {
        syncResult = await CustomerServices.syncBVN(
          {
            bvn: record.uniqueIdentifier ?? null,
            userName: record.userName ?? null,
          },
          true
        );
        if (isFailure(syncResult)) {
          fire("error", syncResult?.message || "BVN sync failed");
          return;
        }
        fire("success", "BVN sync completed successfully");
      }

      const reInitiateResult = await runReInitiate(record);
      if (isFailure(reInitiateResult)) {
        fire("error", reInitiateResult?.message || "Re-initation failed");
        return;
      }
      fire(
        "success",
        reInitiateResult?.message || "Onboarding re-initiated successfully"
      );
      succeeded = true;
    } catch (error) {
      console.error("Error processing record:", error);
      fire("error", "An error occurred while processing this record");
    } finally {
      setActionLoading((prev) => ({ ...prev, [id]: false }));
      setConfirmState(null);
    }

    if (succeeded) {
      await fetchRecords(pagination.pageIndex);
    }
  };

  return (
    <Layout pageTag={"customer"} current={"/onboarding-status-history"}>
      <main>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
          <div className="py-4">
            <div className="sm:flex sm:items-center">
              <div className="sm:flex-auto">
                <h1 className="text-xl font-bold text-gray-900">
                  Onboarding Status History
                </h1>
                <p className="mt-1 text-sm text-gray-500">
                  Failed / pending onboarding verifications by service (NIN,
                  BVN, FLEXCUBE).
                </p>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap items-end gap-3">
              <div className="relative rounded-md shadow-sm">
                <p className="text-sm leading-3 text-gray-500">Start Date</p>
                <DatePicker
                  onChange={(e) => setFormValues({ ...formValues, from: e })}
                  value={formValues.from}
                />
              </div>
              <div className="relative rounded-md shadow-sm">
                <p className="text-sm leading-3 text-gray-500">End Date</p>
                <DatePicker
                  onChange={(e) => setFormValues({ ...formValues, to: e })}
                  value={formValues.to}
                />
              </div>
              <div className="relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <SearchIcon
                    className="h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                </div>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search phone / surname / identifier"
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                />
              </div>
              <button
                type="button"
                onClick={handleDateFilter}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md"
              >
                Search
              </button>
              <button
                type="button"
                onClick={exportCSV}
                disabled={exportLoading}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-green-100 border border-green-300 rounded-md"
              >
                <ArrowCircleDownIcon className="w-4 mr-1.5" />
                {exportLoading ? "Exporting..." : "Export"}
              </button>
            </div>

            <div className="mt-6 relative overflow-hidden ring-1 ring-black ring-opacity-5 md:rounded-lg bg-white">
              <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                  <div className="relative ring-1 ring-black ring-opacity-5 md:rounded-lg bg-white">
                    {loading ? (
                      <div className="flex justify-center py-12">
                        <HollowDotsSpinner size={50} color="#4D627E" />
                      </div>
                    ) : (
                      <table className="min-w-full divide-y divide-gray-300">
                        <thead className="bg-gray-50">
                          <tr>
                            {[
                              {
                                header: "Phone Number",
                                minWidth: "min-w-[120px]",
                              },
                              { header: "Surname", minWidth: "min-w-[120px]" },
                              {
                                header: "Unique Identifier",
                                minWidth: "min-w-[150px]",
                              },
                              {
                                header: "Service Affected",
                                minWidth: "min-w-[120px]",
                              },
                              {
                                header: "Admin Status",
                                minWidth: "min-w-[120px]",
                              },
                              { header: "Reason", minWidth: "min-w-[150px]" },
                              {
                                header: "Created On",
                                minWidth: "min-w-[130px]",
                              },
                              { header: "Actions", minWidth: "min-w-[180px]" },
                            ].map(({ header, minWidth }) => (
                              <th
                                key={header}
                                scope="col"
                                className={`px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 ${minWidth}`}
                              >
                                {header}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                          {records.length === 0 ? (
                            <tr>
                              <td
                                colSpan="8"
                                className="text-center py-8 text-gray-500 text-sm"
                              >
                                No onboarding status history records found
                              </td>
                            </tr>
                          ) : (
                            records.map((record) => {
                              const service = String(
                                record.serviceAffected || ""
                              ).toUpperCase();
                              const status = record.adminStatus || "None";
                              const actionDetails = getActionDetails(record);
                              return (
                                <tr key={record.id} className="bg-gray-50">
                                  <td className="whitespace-nowrap py-3 px-3 text-sm text-gray-900">
                                    {record.phoneNumber || "N/A"}
                                  </td>
                                  <td className="whitespace-nowrap py-3 px-3 text-sm text-gray-900">
                                    {record.surName || "N/A"}
                                  </td>
                                  <td className="whitespace-nowrap py-3 px-3 text-sm text-gray-900">
                                    {record.uniqueIdentifier || "N/A"}
                                  </td>
                                  <td className="whitespace-nowrap py-3 px-3 text-sm">
                                    <span
                                      className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                        serviceStyles[service] ||
                                        "bg-gray-100 text-gray-700"
                                      }`}
                                    >
                                      {record.serviceAffected || "N/A"}
                                    </span>
                                  </td>
                                  <td className="whitespace-nowrap py-3 px-3 text-sm">
                                    <span
                                      className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                        statusStyles[status] ||
                                        "bg-gray-100 text-gray-700"
                                      }`}
                                    >
                                      {record.adminStatus || "N/A"}
                                    </span>
                                  </td>
                                  <td className="py-3 px-3 text-sm text-gray-500 max-w-xs truncate">
                                    {record.reason || "N/A"}
                                  </td>
                                  <td className="whitespace-nowrap py-3 px-3 text-sm text-gray-500">
                                    {record.createdOn || "N/A"}
                                  </td>
                                  <td className="whitespace-nowrap py-3 px-3 text-sm text-right">
                                    <div className="flex justify-end gap-2">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          window.location.href = `/onboarding-status-history/view?id=${record.id}`;
                                        }}
                                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-100"
                                      >
                                        View
                                      </button>
                                      {isMaker ? (
                                        record.adminStatus === "Initiated" ? (
                                          <span className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-500 bg-blue-100 border border-blue-300 rounded-md">
                                            Initiated, waiting on checker
                                          </span>
                                        ) : (
                                          actionDetails?.sync && (
                                            <button
                                              type="button"
                                              disabled={actionLoading[record.id]}
                                              onClick={() => openConfirm(record)}
                                              className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-slate-600 hover:bg-slate-700 rounded-md disabled:opacity-60"
                                            >
                                              {actionLoading[record.id] ? (
                                                <HollowDotsSpinner
                                                  size={12}
                                                  color="#FFFFFF"
                                                />
                                              ) : (
                                                <ArrowRightIcon className="w-3 mr-1" />
                                              )}
                                              {actionDetails.sync.label}
                                            </button>
                                          )
                                        )
                                      ) : (
                                        actionDetails?.sync && (
                                          <button
                                            type="button"
                                            disabled={actionLoading[record.id]}
                                            onClick={() => openConfirm(record)}
                                            className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-slate-600 hover:bg-slate-700 rounded-md disabled:opacity-60"
                                          >
                                            {actionLoading[record.id] ? (
                                              <HollowDotsSpinner
                                                size={12}
                                                color="#FFFFFF"
                                              />
                                            ) : (
                                              <ArrowRightIcon className="w-3 mr-1" />
                                            )}
                                            {actionDetails.sync.label}
                                          </button>
                                        )
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {records.length > 0 && !loading && (
              <div className="mt-4">
                <Paginate
                  nextPage={pagination.hasNextPage}
                  pageIndex={pagination.pageIndex}
                  totalCount={pagination.totalCount}
                  totalPages={pagination.totalPages}
                  prevPage={pagination.hasPreviousPage}
                  setPageIndex={handlePageChange}
                />
              </div>
            )}
          </div>
        </div>
      </main>

      <ConfirmModal
        isOpen={!!confirmState}
        onClose={() => setConfirmState(null)}
        onConfirm={confirmAction}
        title={confirmState?.details?.title || "Confirm"}
        message={confirmState?.details?.message || ""}
        confirmText="Proceed"
        cancelText="Cancel"
        isLoading={actionLoading[confirmState?.record?.id]}
      />
    </Layout>
  );
} 